import * as THREE from 'three'

export interface Indication3DLocation {
  startX: number
  endX: number
  startY: number
  endY: number
  startZ: number
  endZ: number
}

export interface Indication3DZoomEvent extends CustomEvent {
  detail: {
    location: Indication3DLocation
    indicationId: string
  }
}

export interface Indication3DCustomInfo {
  location?: Indication3DLocation
  [key: string]: unknown
}

export interface Indication3DAnimationState {
  isAnimating: boolean
  startTime: number
  duration: number
  startCameraPosition: THREE.Vector3
  endCameraPosition: THREE.Vector3
  startTarget: THREE.Vector3
  endTarget: THREE.Vector3
  startZoom?: number
  endZoom?: number
}

export const isValidIndication3DLocation = (
  location: unknown
): location is Indication3DLocation => {
  if (!location || typeof location !== 'object') return false

  const loc = location as Record<string, unknown>
  const requiredFields = ['startX', 'endX', 'startY', 'endY', 'startZ', 'endZ']

  return requiredFields.every((field) => {
    const value = loc[field]
    return Number.isFinite(value)
  })
}

export const easeInCubic = (t: number): number => t * t * t
export const INDICATION_3D_ZOOM_EVENT = 'indication-3d-zoom'

export const calculateLocationBounds = (location: Indication3DLocation) => {
  const minX = Math.min(location.startX, location.endX)
  const maxX = Math.max(location.startX, location.endX)
  const minY = Math.min(location.startY, location.endY)
  const maxY = Math.max(location.startY, location.endY)
  const minZ = Math.min(location.startZ, location.endZ)
  const maxZ = Math.max(location.startZ, location.endZ)

  const width = maxX - minX
  const height = maxY - minY
  const depth = maxZ - minZ

  const centerX = (minX + maxX) / 2
  const centerY = (minY + maxY) / 2
  const centerZ = (minZ + maxZ) / 2

  return {
    bounds: { minX, maxX, minY, maxY, minZ, maxZ },
    dimensions: { width, height, depth },
    center: { x: centerX, y: centerY, z: centerZ },
    maxSize: Math.max(width, height, depth),
  }
}
